import React from 'react';

import { ReactComponent as CalendarIcon } from 'assets/pics/Calendar.svg';
import { ReactComponent as ClockIcon } from 'assets/pics/clock.svg';
import { ReactComponent as DownloadIcon } from 'assets/pics/downloadIcon.svg';
import { ReactComponent as EuroIcon } from 'assets/pics/euroIcon.svg';
import LocationIcon from 'assets/pics/locationIcon.svg';
import { ReactComponent as ParticipantsIcon } from 'assets/pics/participantsIcon.svg';
import TickIcon from 'assets/pics/tick.svg';
import PropTypes from 'prop-types';

import { FORM_CONSTANTS } from 'global/constants';

import { getIsRegistrationExpired } from 'utils/EventDateUtils';
import { getSanitzedAttachmentUrl } from 'utils/UrlUtil';

import {
  formatAvailabilityParts,
  formatEventDayParts,
  formatFeeParts,
  formatLocationParts,
  formatRegistrationDeadlineParts,
  getEventDaysForDisplay,
} from './formatEventCardMetaData';

import './EventCardMetaData.scss';

const metaDataPartsPropType = PropTypes.shape({
  label: PropTypes.string,
  value: PropTypes.string.isRequired,
});

const MetaDataText = ({ label, value }) => (
  <div className="event-card-meta-data__text">
    {label && <span className="event-card-meta-data__label">{`${label} `}</span>}
    <span className="event-card-meta-data__value">{value}</span>
  </div>
);

MetaDataText.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string.isRequired,
};

const MetaDataItem = ({ icon, parts }) => (
  <div className="event-card-meta-data__section">
    <div className="event-card-meta-data__icon-container">{icon}</div>
    <MetaDataText label={parts.label} value={parts.value} />
  </div>
);

MetaDataItem.propTypes = {
  icon: PropTypes.node.isRequired,
  parts: metaDataPartsPropType.isRequired,
};

export const EventCardMetaData = ({ event }) => {
  const {
    eventDays = [],
    location = {},
    meetingLink,
    registrationDeadline,
    registration = { participants: [], eventParticipantsNumber: 0 },
    attachmentUrl,
    maxParticipants,
    disableMaxParticipation = false,
    tags = [],
    chargingAmount,
  } = event;

  const { participants = [], eventParticipantsNumber = 0 } = registration;
  const isRegistrationExpired = getIsRegistrationExpired(event);
  const daysForDisplay = getEventDaysForDisplay(eventDays);

  const availabilityParts = formatAvailabilityParts({
    maxParticipants,
    eventParticipantsNumber,
    isRegistrationExpired,
    disableMaxParticipation,
  });

  const registrationDeadlineParts = formatRegistrationDeadlineParts(registrationDeadline);
  const locationParts = formatLocationParts(location, meetingLink);

  return (
    <div className="event-card-meta-data">
      {daysForDisplay.map((eventDay) => (
        <MetaDataItem
          key={`${eventDay.date}-${eventDay.startTime}-${eventDay.endTime}`}
          icon={<CalendarIcon alt="" className="event-card-meta-data__icon" />}
          parts={formatEventDayParts(eventDay)}
        />
      ))}

      {locationParts?.value && (
        <MetaDataItem
          icon={<img alt="" src={LocationIcon} className="event-card-meta-data__icon" />}
          parts={locationParts}
        />
      )}

      {registrationDeadlineParts && (
        <MetaDataItem
          icon={<ClockIcon alt="" className="event-card-meta-data__icon" />}
          parts={registrationDeadlineParts}
        />
      )}

      {attachmentUrl && participants.length > 0 && (
        <div className="event-card-meta-data__section event-card-meta-data__section--download">
          <div className="event-card-meta-data__icon-container">
            <DownloadIcon
              alt=""
              className="event-card-meta-data__icon event-card-meta-data__icon--link"
            />
          </div>
          <a
            className="event-card-meta-data__link"
            href={getSanitzedAttachmentUrl(attachmentUrl)}
            download>
            Anhang
          </a>
        </div>
      )}

      {availabilityParts && (
        <MetaDataItem
          icon={<ParticipantsIcon alt="" className="event-card-meta-data__icon" />}
          parts={availabilityParts}
        />
      )}

      {tags.includes(FORM_CONSTANTS.TAGS_EVENTS.KOSTENPFLICHTIG) && (
        <MetaDataItem
          icon={
            <EuroIcon
              alt=""
              className="event-card-meta-data__icon event-card-meta-data__icon--fill"
            />
          }
          parts={formatFeeParts(chargingAmount)}
        />
      )}
    </div>
  );
};

EventCardMetaData.propTypes = {
  event: PropTypes.shape({
    eventDays: PropTypes.arrayOf(
      PropTypes.shape({
        date: PropTypes.string.isRequired,
        startTime: PropTypes.string,
        endTime: PropTypes.string,
      })
    ),
    location: PropTypes.shape({
      description: PropTypes.string,
      name: PropTypes.string,
      street: PropTypes.string,
      streetNumber: PropTypes.string,
      zip: PropTypes.string,
      city: PropTypes.string,
    }),
    meetingLink: PropTypes.string,
    registrationDeadline: PropTypes.string,
    registration: PropTypes.shape({
      participants: PropTypes.arrayOf(PropTypes.shape({})),
      eventParticipantsNumber: PropTypes.number,
    }),
    attachmentUrl: PropTypes.string,
    maxParticipants: PropTypes.number,
    disableMaxParticipation: PropTypes.bool,
    tags: PropTypes.arrayOf(PropTypes.string),
    chargingAmount: PropTypes.string,
  }).isRequired,
};
